// Glass plates — which products are the NEW GLASS SERIES, and what each plate
// looks like, so the desktop can draw the real plate instead of a card.
//
// ⚠️ DO NOT MIX THE TWO SERIES. The same function exists in both — the old
// 2-module 4 Touch (DeviceID 49, GM / Cona / Gold bodies) and a glass 4 Touch —
// and they are SEPARATE DeviceTypes. A type is a glass plate ONLY when its own
// DeviceType row says so; nothing is inferred from a switch count or a name.
// (2026-10-05: 49 / 53 / 55 / 56 had been drawn as glass by mistake.)
//
// The facts live in the DeviceType table — set on the Device Types admin page:
//   GlassModules  0 = not glass; 2 / 4 / 6 / 8 = the plate's module size
//   GlassMaster   the plate has a physical "||" master button. NOT TuyaMasterDp:
//                 49 and 92 carry a master DP and have no master button.
//   GlassSocket   a 3-pin socket on the plate — drawn, never controlled
//   On_off / Dimming   the switches and fans, as for every panel
// (Home Automation glass_device_type_migration.sql). A NEW glass type therefore
// needs no app change: create it, set its modules, and it draws as a plate.
//
// FALLBACK, until that migration has run (the catalog then has no GlassModules
// at all): the glass types that exist today, owner-confirmed from the price
// sheet and photos. Once the column exists the TABLE wins, always.
//
// Layout rules, from the product photos:
//   • switches are round buttons in TWO ROWS, filling DOWN each column — the
//     wall order 1 3 5 / 2 4 6 that core/devices/buttonLayout already encodes
//   • the MASTER ("||") takes the LAST position — bottom of the last column
//   • a FAN is a column of three buttons (+, fan, −) with its LED speed strip
//   • a SOCKET is a square 3-pin socket at the right

import { deviceCatalogEntry } from './deviceCatalog';

export const MODULE_SIZES = [2, 4, 6, 8];

/** DeviceID -> { modules, master, socket, switches, fans } — fallback only. */
const FALLBACK = {
  87: { modules: 8, master: true, socket: false, switches: 7, fans: 2 },  // 7 + Master + 2 Fans
  92: { modules: 4, master: false, socket: false, switches: 4, fans: 1 }, // 4 + Fan
  93: { modules: 8, master: true, socket: true, switches: 7, fans: 1 },   // 7 + Master + Fan + Socket
  94: { modules: 8, master: true, socket: false, switches: 9, fans: 0 },  // 9 + Master
  95: { modules: 4, master: true, socket: false, switches: 5, fans: 0 },  // 5 + Master
  96: { modules: 6, master: true, socket: true, switches: 7, fans: 0 },   // 7 + Master + Socket
  97: { modules: 8, master: true, socket: true, switches: 9, fans: 0 },   // 9 + Master + Socket
  98: { modules: 8, master: true, socket: true, switches: 7, fans: 0 },   // 7 + Master + Socket
  99: { modules: 2, master: false, socket: false, switches: 1, fans: 0 }, // 20A (= 30A) Switch
};

const shape = ({ modules, master, socket, switches, fans }) => {
  const m = Number(modules);
  const sw = Math.max(0, Number(switches) || 0);
  const fn = Math.max(0, Number(fans) || 0);
  if (!MODULE_SIZES.includes(m) || sw + fn === 0) return null;
  return {
    modules: m,
    switches: sw,
    master: !!master,
    fans: fn,
    socket: !!socket,
    // A glass FAN plate (no switches) speaks the standalone fan's ladder.
    standaloneFan: sw === 0 && fn > 0,
  };
};

/**
 * -> the plate for a DeviceID, or null (not glass — keep the ordinary card).
 * The DeviceType table decides once it carries GlassModules; until then, the
 * fallback list of today's glass types.
 */
export function plateFor(deviceId) {
  const e = deviceCatalogEntry(deviceId);
  if (e && e.glassModules != null) {
    if (!(Number(e.glassModules) > 0)) return null;
    return shape({
      modules: e.glassModules,
      master: e.glassMaster,
      socket: e.glassSocket,
      switches: e.onOff,
      fans: e.dimming,
    });
  }
  const f = FALLBACK[Number(deviceId)];
  return f ? shape(f) : null;
}

export const isGlassPlate = (deviceId) => plateFor(deviceId) != null;

/**
 * The positions of the switch area, column by column, top then bottom — the
 * wall order. Each slot is { kind: 'switch', index } (0-based relay channel) or
 * { kind: 'master' }. Below four positions the plate is one row side by side.
 * -> array of columns, each an array of 1–2 slots.
 */
export function switchColumns(plate) {
  const slots = [];
  for (let i = 0; i < plate.switches; i += 1) slots.push({ kind: 'switch', index: i });
  if (plate.master) slots.push({ kind: 'master' });
  if (slots.length === 0) return [];
  if (slots.length < 4) return slots.map((s) => [s]);
  const cols = [];
  for (let i = 0; i < slots.length; i += 2) cols.push(slots.slice(i, i + 2));
  return cols;
}

/** Card width (px) and room-grid span — wider plates need the room. */
export const PLATE_CARD_WIDTH = { 2: 280, 4: 400, 6: 520, 8: 640 };

export function plateGridSpan(modules) {
  if (modules >= 8) return { xs: 12, md: 12, lg: 6 };
  if (modules >= 6) return { xs: 12, md: 8, lg: 5 };
  if (modules >= 4) return { xs: 12, sm: 8, md: 6, lg: 4 };
  return { xs: 12, sm: 6, md: 4, lg: 3 };
}
